import {
  AGE_BAND_OPTIONS,
  COMMUNICATION_CATEGORY_OPTIONS,
  COMMUNICATION_OUTCOME_OPTIONS,
  COMMUNICATION_PARTICIPANT_OPTIONS,
  HILLOS,
  LEARNER_GRADE_OPTIONS,
  isHilloNumber,
} from '@iculogbook/shared';
import type { CommunicationParticipant, LearnerGrade } from '@iculogbook/shared';

import {
  Button,
  ChipGroup,
  Disclosure,
  Field,
  NO_IDENTIFIABLE_INFORMATION,
  TextArea,
} from '../../components/index.ts';
import { CapabilityLevelField } from './CapabilityLevelField.tsx';
import { OccurredAtField } from './OccurredAtField.tsx';
import type { FormErrors } from './ProcedureForm.tsx';
import type { CommunicationFormValues } from './communicationForm.ts';

const NOTE_MAX_LENGTH = 500;

type CommunicationFormProps = {
  values: CommunicationFormValues;
  onChange: (values: CommunicationFormValues) => void;
  onSave: () => void;
  /** Saves, then opens a new form prefilled from what was just saved. */
  onSaveAndDuplicate: () => void;
  saving: boolean;
  errors: FormErrors;
  /** Editing a stored entry rather than logging a new one. */
  editing?: boolean;
};

/**
 * The Communication form: a significant discussion, most often withdrawal or
 * withholding of treatment.
 *
 * Shaped like the procedure form rather than EDT's — there is a patient, so
 * age band and capability level sit here on the same terms as everywhere
 * else. `withWhom` is a fixed list of roles, never a text box, because a free
 * field here is the one most likely to invite a name — see CLAUDE.md rule 1.
 */
export function CommunicationForm({
  values,
  onChange,
  onSave,
  onSaveAndDuplicate,
  saving,
  errors,
  editing = false,
}: CommunicationFormProps) {
  function set<K extends keyof CommunicationFormValues>(
    key: K,
    value: CommunicationFormValues[K],
  ): void {
    onChange({ ...values, [key]: value });
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        onSave();
      }}
      className="flex flex-col gap-4"
      noValidate
    >
      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-text">What kind of discussion</span>
        <ChipGroup
          label="What kind of discussion"
          options={COMMUNICATION_CATEGORY_OPTIONS.map((option) => ({
            value: option.key,
            label: option.label,
          }))}
          value={values.category}
          onChange={(next) => set('category', next)}
        />
        {errors['category'] === undefined ? null : (
          <p role="alert" className="text-xs text-danger">
            {errors['category']}
          </p>
        )}
      </div>

      <CapabilityLevelField
        value={values.capabilityLevel}
        onChange={(level) => set('capabilityLevel', level)}
        error={errors['capabilityLevel']}
      />

      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-text">Age band</span>
        <ChipGroup
          label="Age band"
          options={AGE_BAND_OPTIONS.map((option) => ({ value: option.key, label: option.label }))}
          value={values.ageBand}
          onChange={(next) => set('ageBand', next)}
        />
        {errors['ageBand'] === undefined ? null : (
          <p role="alert" className="text-xs text-danger">
            {errors['ageBand']}
          </p>
        )}
      </div>

      <OccurredAtField
        value={values.occurredAt}
        onChange={(next) => set('occurredAt', next)}
        error={errors['occurredAt']}
      />

      {/*
        Roles, never a text box — this is the field most likely to invite a
        family member's name, so it is not free text. See CLAUDE.md rule 1.
      */}
      <Disclosure
        title="Who was there"
        hint="Optional. Roles only, never names."
        badge={values.withWhom.length === 0 ? null : `${String(values.withWhom.length)} chosen`}
      >
        <ChipGroup
          label="Who was there"
          multiple
          options={COMMUNICATION_PARTICIPANT_OPTIONS.map((option) => ({
            value: option.key,
            label: option.label,
          }))}
          value={values.withWhom}
          onChange={(next) => set('withWhom', next as CommunicationParticipant[])}
        />
      </Disclosure>

      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-text">
          Outcome <span className="font-normal text-faint">(optional)</span>
        </span>
        <ChipGroup
          label="Outcome"
          options={COMMUNICATION_OUTCOME_OPTIONS.map((option) => ({
            value: option.key,
            label: option.label,
          }))}
          value={values.outcome}
          onChange={(next) => set('outcome', next)}
        />
      </div>

      <Disclosure
        title="Teaching or supervising"
        hint="Optional. Who you supervised through this conversation."
        badge={
          values.teachingGrades.length === 0
            ? null
            : `${String(values.teachingGrades.length)} chosen`
        }
      >
        <ChipGroup
          label="Who you supervised"
          multiple
          options={LEARNER_GRADE_OPTIONS.map((option) => ({
            value: option.key,
            label: option.label,
          }))}
          value={values.teachingGrades}
          onChange={(next) => set('teachingGrades', next as LearnerGrade[])}
        />
      </Disclosure>

      <Field label="Notes" help={NO_IDENTIFIABLE_INFORMATION} error={errors['note']}>
        <TextArea
          value={values.note}
          onChange={(event) => set('note', event.target.value)}
          maxLength={NOTE_MAX_LENGTH}
          rows={5}
          placeholder="How the conversation went, anything to follow up."
        />
      </Field>

      <Disclosure
        title="Curriculum"
        hint="Optional HiLLO tagging. Nothing is stored until you tap."
        badge={values.hillos.length === 0 ? null : `${String(values.hillos.length)} tagged`}
      >
        <ChipGroup
          label="High Level Learning Outcomes"
          multiple
          options={HILLOS.map((hillo) => ({
            value: String(hillo.number),
            label: `${String(hillo.number)}. ${hillo.shortLabel}`,
          }))}
          value={values.hillos.map(String)}
          onChange={(next) => set('hillos', next.map(Number).filter(isHilloNumber))}
        />
      </Disclosure>

      <Button type="submit" variant="primary" block disabled={saving} className="h-14">
        {editing ? 'Save changes' : 'Save entry'}
      </Button>

      <Button
        variant="secondary"
        block
        disabled={saving}
        onClick={onSaveAndDuplicate}
        className="h-14"
      >
        {editing ? 'Save changes and duplicate' : 'Save and duplicate'}
      </Button>
    </form>
  );
}
